/**
 * Emergency Contact List Tab Component
 * Auto-generated report for compliance - State requirement for First Aid Kit
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { AlertTriangle, Printer, Download, Loader2, Phone, Mail, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EmergencyContactListTab = ({ tableOnly = false }) => {
  const { organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/children`);
      setChildren(res.data || []);
    } catch (error) {
      console.error("Error fetching children:", error);
      toast.error("Failed to load children data");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Emergency Contact List - ${organization?.name || 'Center'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; max-width: 1000px; margin: 0 auto; }
          .logo-header { margin-bottom: 8px; }
          .logo-header img { height: 50px; width: auto; }
          .center-name { text-align: center; font-size: 22px; font-weight: bold; color: #1F2937; margin-bottom: 15px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #dc3545; padding-bottom: 15px; }
          .report-title { font-size: 16px; color: #dc3545; font-weight: bold; }
          .state-req { font-size: 11px; color: #666; font-style: italic; margin-top: 5px; }
          .date { font-size: 12px; color: #888; margin-top: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 10px; }
          th { background: #dc3545; color: white; padding: 8px; text-align: left; }
          td { padding: 6px 8px; border-bottom: 1px solid #ddd; vertical-align: top; }
          tr:nth-child(even) { background: #fff5f5; }
          .child-name { font-weight: bold; }
          .contact-info { font-size: 9px; }
          .emergency { color: #dc3545; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #888; border-top: 1px solid #ddd; padding-top: 10px; }
          .first-aid-note { background: #fff3cd; padding: 10px; border-radius: 5px; margin-top: 15px; font-size: 11px; }
          @media print { 
            body { padding: 15px; } 
            .first-aid-note { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="logo-header"><img src="/simple-sign-in-logo.png" alt="Simple Sign In" onerror="this.style.display='none'" /></div>
        <div class="center-name">${organization?.name || 'Childcare Center'}</div>
        <div class="header">
          <div class="report-title">EMERGENCY CONTACT LIST</div>
          <div class="state-req">State Requirement - Must be kept in First Aid Kit</div>
          <div class="date">Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 15%">Child Name</th>
              <th style="width: 8%">DOB</th>
              <th style="width: 20%">Parent/Guardian 1</th>
              <th style="width: 20%">Parent/Guardian 2</th>
              <th style="width: 20%">Emergency Contact</th>
              <th style="width: 17%">Medical/Allergies</th>
            </tr>
          </thead>
          <tbody>
            ${children.map(child => {
              const parent1 = child.pickupPersons?.[0] || {};
              const parent2 = child.pickupPersons?.[1] || {};
              const emergency = child.emergencyContacts?.[0] || {};
              return `
                <tr>
                  <td class="child-name">${child.name || ''}</td>
                  <td>${child.birthdate ? new Date(child.birthdate).toLocaleDateString() : 'N/A'}</td>
                  <td class="contact-info">
                    <strong>${parent1.name || 'N/A'}</strong><br/>
                    ${parent1.phone || 'No phone'}<br/>
                    ${parent1.email || ''}
                  </td>
                  <td class="contact-info">
                    <strong>${parent2.name || ''}</strong><br/>
                    ${parent2.phone || ''}<br/>
                    ${parent2.email || ''}
                  </td>
                  <td class="contact-info emergency">
                    <strong>${emergency.name || 'N/A'}</strong><br/>
                    ${emergency.phone || 'No phone'}<br/>
                    Relation: ${emergency.relationship || 'N/A'}
                  </td>
                  <td class="contact-info">
                    ${child.specialInstructions || 'None noted'}
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        <div class="first-aid-note">
          <strong>⚠️ FIRST AID KIT REQUIREMENT:</strong> This Emergency Contact List must be kept in the First Aid Kit and updated regularly per state licensing requirements.
        </div>
        <div class="footer">
          <p>Total Children: ${children.length} | This list is confidential - for emergency use only</p>
          <p>${organization?.name || 'Center'} | ${organization?.address || ''}</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const renderTable = () => (
    <div className="border rounded-lg overflow-hidden" data-testid="emergency-contact-table">
      <table className="w-full text-sm">
        <thead className="bg-red-600 text-white">
          <tr>
            <th className="px-3 py-2 text-left font-bold">Child Name</th>
            <th className="px-3 py-2 text-left font-bold">Parent/Guardian</th>
            <th className="px-3 py-2 text-left font-bold">Emergency Contact</th>
            <th className="px-3 py-2 text-left font-bold">Phone</th>
          </tr>
        </thead>
        <tbody>
          {children.slice(0, 5).map((child, idx) => {
            const parent1 = child.parents?.[0] || child.guardian || {};
            const emergency = child.emergencyContacts?.[0] || {};
            return (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-red-50'}>
                <td className="px-3 py-2 font-medium">
                  {child.firstName} {child.lastName}
                </td>
                <td className="px-3 py-2">
                  {parent1.name || parent1.firstName || 'N/A'} {parent1.lastName || ''}
                </td>
                <td className="px-3 py-2 text-red-600">
                  {emergency.name || 'N/A'}
                </td>
                <td className="px-3 py-2">
                  {emergency.phone || emergency.cellPhone || 'N/A'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {children.length > 5 && (
        <div 
          className="bg-gray-50 px-3 py-2 text-center text-sm text-gray-500 cursor-pointer hover:text-gray-700 hover:bg-gray-100 transition-colors"
          onClick={handlePrint}
          data-testid="emergency-contact-print-footer"
        >
          + {children.length - 5} more children (click Print to see full list)
        </div>
      )}
    </div>
  );

  if (loading) {
    return tableOnly ? (
      <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
    ) : (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (tableOnly) {
    return renderTable();
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Emergency Contact List
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Download className="w-4 h-4 mr-1" />
              Download
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          State requirement - must be kept in First Aid Kit
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-800">
            <strong>⚠️ First Aid Kit Requirement:</strong> This Emergency Contact List must be printed and kept in your First Aid Kit per state licensing requirements.
          </p>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <Badge variant="outline" className="gap-1">
            <Users className="w-3 h-3" />
            {children.length} Children
          </Badge>
          <span className="text-gray-500">Last updated: {new Date().toLocaleDateString()}</span>
        </div>

        {renderTable()}
      </CardContent>
    </Card>
  );
};

export default EmergencyContactListTab;
